/**
 * Pure geometry for the unified tutorial spotlight system.
 *
 * The overlay is one fixed full-viewport element whose clip-path is an
 * evenodd path: the outer viewport rectangle minus one rounded "hole" per
 * highlighted region. Painted (clipped-in) area = dimmed AND pointer-blocking;
 * holes = fully transparent AND pointer-transparent (hit-testing respects
 * clip-path), so clicks over a hole reach the real element underneath.
 * No transparent full-screen layer ever intercepts spotlight clicks.
 *
 * All functions are pure so the sizing/placement CONTRACT is unit-testable
 * without a browser (same discipline as stageSquare.ts).
 */

export interface SpotRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Viewport {
  w: number;
  h: number;
}

/** Expand a target rect by `pad` on every side, clamped to the viewport. */
export function padRect(r: SpotRect, pad: number, vp: Viewport): SpotRect {
  const x = Math.max(0, r.x - pad);
  const y = Math.max(0, r.y - pad);
  return {
    x,
    y,
    w: Math.min(vp.w, r.x + r.w + pad) - x,
    h: Math.min(vp.h, r.y + r.h + pad) - y,
  };
}

/** One rounded-rect subpath (clockwise), radius clamped to half the sides. */
function roundedRectPath(r: SpotRect, radius: number): string {
  const rad = Math.max(0, Math.min(radius, r.w / 2, r.h / 2));
  const x2 = r.x + r.w;
  const y2 = r.y + r.h;
  return (
    `M ${r.x + rad} ${r.y} ` +
    `L ${x2 - rad} ${r.y} A ${rad} ${rad} 0 0 1 ${x2} ${r.y + rad} ` +
    `L ${x2} ${y2 - rad} A ${rad} ${rad} 0 0 1 ${x2 - rad} ${y2} ` +
    `L ${r.x + rad} ${y2} A ${rad} ${rad} 0 0 1 ${r.x} ${y2 - rad} ` +
    `L ${r.x} ${r.y + rad} A ${rad} ${rad} 0 0 1 ${r.x + rad} ${r.y} Z`
  );
}

/**
 * Evenodd clip path: `outer` filled, minus a rounded hole per entry of
 * `holes`. Used both for the full-viewport blocking layer (outer = viewport)
 * and for the pointer-transparent passthrough tint (outer = its own rect,
 * holes = any bright cutouts inside it).
 */
export function buildCutoutPath(outer: SpotRect, holes: SpotRect[], radius: number): string {
  const outerPath =
    `M ${outer.x} ${outer.y} L ${outer.x + outer.w} ${outer.y} ` +
    `L ${outer.x + outer.w} ${outer.y + outer.h} L ${outer.x} ${outer.y + outer.h} Z`;
  return [outerPath, ...holes.map((h) => roundedRectPath(h, radius))].join(' ');
}

export function rectsIntersect(a: SpotRect, b: SpotRect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function unionRect(a: SpotRect, b: SpotRect): SpotRect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x, y,
    w: Math.max(a.x + a.w, b.x + b.w) - x,
    h: Math.max(a.y + a.h, b.y + b.h) - y,
  };
}

/**
 * Merge intersecting rects into their bounding unions.
 *
 * REQUIRED for the even-odd dim layer: a hole nested inside (or overlapping)
 * another hole toggles the fill BACK ON, silently re-painting — and
 * re-blocking — the inner region (the "tile inside the passthrough rack"
 * case). The dim must therefore punch only disjoint holes; the passthrough
 * tint layer re-dims its own area minus the bright cutouts afterwards, so
 * the visual emphasis is preserved.
 */
export function mergeOverlappingRects(rects: SpotRect[]): SpotRect[] {
  const out: SpotRect[] = [];
  for (const r of rects) {
    let merged = { ...r };
    let changed = true;
    while (changed) {
      changed = false;
      for (let i = 0; i < out.length; i++) {
        if (rectsIntersect(out[i], merged)) {
          merged = unionRect(out.splice(i, 1)[0], merged);
          changed = true;
          break;
        }
      }
    }
    out.push(merged);
  }
  return out;
}

export type CoachPlacement = 'below' | 'above' | 'right' | 'left' | 'bottom-bar' | 'top-bar';

export interface CoachPosition {
  left: number;
  top: number;
  placement: CoachPlacement;
}

const GAP = 16;
const EDGE = 12;

/**
 * Choose a coach-card position that never covers any highlighted region.
 *
 * Desktop: try below → above → right → left of the union of the target
 * rects; the first candidate that fits fully inside the viewport AND
 * intersects no target wins; otherwise fall back to the corner farthest
 * from the targets (clamped, still non-overlapping in practice because the
 * fallback picks the emptier half).
 *
 * Mobile: a compact bar pinned to whichever vertical half the targets do
 * NOT occupy (targets in the top half → bottom bar, and vice versa).
 */
export function placeCoachCard(
  targets: SpotRect[],
  card: { w: number; h: number },
  vp: Viewport,
  isMobileLayout: boolean,
): CoachPosition {
  if (targets.length === 0) {
    // No spotlight (e.g. "watch the table") — bottom-center, unobtrusive.
    return {
      left: Math.max(EDGE, (vp.w - card.w) / 2),
      top: Math.max(EDGE, vp.h - card.h - EDGE),
      placement: 'bottom-bar',
    };
  }

  const minX = Math.min(...targets.map((t) => t.x));
  const minY = Math.min(...targets.map((t) => t.y));
  const maxX = Math.max(...targets.map((t) => t.x + t.w));
  const maxY = Math.max(...targets.map((t) => t.y + t.h));
  const union: SpotRect = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  const centerY = union.y + union.h / 2;

  if (isMobileLayout) {
    const bottom = centerY < vp.h / 2;
    return {
      left: Math.max(EDGE, (vp.w - card.w) / 2),
      top: bottom ? Math.max(EDGE, vp.h - card.h - EDGE) : EDGE,
      placement: bottom ? 'bottom-bar' : 'top-bar',
    };
  }

  const clampX = (x: number) => Math.min(Math.max(x, EDGE), Math.max(EDGE, vp.w - card.w - EDGE));
  const clampY = (y: number) => Math.min(Math.max(y, EDGE), Math.max(EDGE, vp.h - card.h - EDGE));

  const candidates: CoachPosition[] = [
    { placement: 'below', left: clampX(union.x + union.w / 2 - card.w / 2), top: union.y + union.h + GAP },
    { placement: 'above', left: clampX(union.x + union.w / 2 - card.w / 2), top: union.y - GAP - card.h },
    { placement: 'right', left: union.x + union.w + GAP, top: clampY(union.y + union.h / 2 - card.h / 2) },
    { placement: 'left',  left: union.x - GAP - card.w,  top: clampY(union.y + union.h / 2 - card.h / 2) },
  ];

  for (const c of candidates) {
    const rect: SpotRect = { x: c.left, y: c.top, w: card.w, h: card.h };
    const inViewport =
      c.left >= 0 && c.top >= 0 && c.left + card.w <= vp.w && c.top + card.h <= vp.h;
    if (inViewport && !targets.some((t) => rectsIntersect(rect, t))) return c;
  }

  // Fallback: pin to the emptier vertical half, horizontally clamped.
  const bottomHalf = centerY < vp.h / 2;
  return {
    left: clampX(union.x + union.w / 2 - card.w / 2),
    top: bottomHalf ? clampY(vp.h - card.h - EDGE) : EDGE,
    placement: bottomHalf ? 'bottom-bar' : 'top-bar',
  };
}
